<script lang="ts" setup>
/**
 * Listing (SRP) mobile search modal. For Shorts use ShortsSearchSuggestDialog — different close/clear rules.
 */
import type { NewSearchSuggestProps } from '.';
import type { SearchSuggestDialogEmits } from '../../composables/useNewSearchSuggest';
import type { SearchSuggestDialogPanelHostProps } from './searchSuggestDialogPanelProps';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';

import { useSingleFieldSuggest } from '~/composables/useSingleFieldSuggest';
import { cn } from '~/utils';

import IconMaterialSymbolsLightCancel from '~icons/material-symbols-light/cancel';
import IconMaterialSymbolsLightSearch from '~icons/material-symbols-light/search';

import { SEARCH_PLACEHOLDERS } from '.';
import { useNewSearchSuggest } from '../../composables/useNewSearchSuggest';
import { useDeferredInputFocus } from './composables/useDeferredInputFocus';
import { useSearchSuggestDialogPlaceholderAnimation } from './composables/useSearchSuggestDialogPlaceholderAnimation';
import { useSearchSuggestDialogScrollLock } from './composables/useSearchSuggestDialogScrollLock';
import { useSearchSuggestDialogSharedHandlers } from './composables/useSearchSuggestDialogSharedHandlers';
import SearchSuggestDialogPanel from './SearchSuggestDialogPanel.vue';
import { getSearchSuggestVariantStyles } from './variants';

const props = withDefaults(
	defineProps<NewSearchSuggestProps & { placeholderClasses?: string }>(),
	{
		isFetchingPropertyByCodeSearch: false,
		placeholders: () => [...SEARCH_PLACEHOLDERS.animated],
		showInputButton: true,
		variant: 'primary'
	}
);

const variantStyles = computed(() =>
	getSearchSuggestVariantStyles(props.variant)
);

const emit = defineEmits<SearchSuggestDialogEmits>();

const isOpen = defineModel<boolean>('isOpen', { required: true });
const selectedValue = ref<SuggestionGroup>();

const effectivePlaceholders = computed(() => [SEARCH_PLACEHOLDERS.dialog]);

const normalizedPlaceholder = computed<string | undefined>(() => {
	const placeholders = props.placeholders;
	if (typeof placeholders === 'string') {
		return placeholders;
	}
	if (Array.isArray(placeholders)) {
		return placeholders[0];
	}
	return undefined;
});

const { selectedMacroregion: globalSelectedMacroregion } =
	useSingleFieldSuggest();

const {
	searchTerm,
	modelValue,
	isUpdatingFromWatch,
	hasMinSearchTerm,
	searchByCodeOption,
	shouldShowCodeGroup,
	shouldShowEmptyState,
	shouldShowLastResearchs,
	locationAutocompleteData,
	loading,
	dynamicPlaceholder,
	placeholders: computedPlaceholders,
	filteredLastResearchs,
	removeResearchs,
	clear,
	inputClasses,
	getDisplayValue,
	handleKeyDown,
	handleModelValueUpdate: baseHandleModelValueUpdate,
	executeLocationAutocomplete,
	handleApplySearchTerm
} = useNewSearchSuggest({
	props,
	emit,
	isDropdownOpen: isOpen,
	macroregion: globalSelectedMacroregion,
	providers: props.providers,
	selectOnly: props.selectOnly,
	bus: props.bus
});

const { restoreBodyScroll } = useSearchSuggestDialogScrollLock(isOpen);

const {
	animatedPlaceholder,
	validateAndStartAnimation,
	pause: pausePlaceholderAnimation
} = useSearchSuggestDialogPlaceholderAnimation(effectivePlaceholders);

function closeDialog() {
	if (!modelValue.value && props.searchTerm) {
		handleApplySearchTerm();
	}
	isOpen.value = false;

	if (!searchTerm.value && !modelValue.value && !props.searchTerm) {
		validateAndStartAnimation();
	}
}

const { openDialog, handleModelValueUpdate, handleInputClear } =
	useSearchSuggestDialogSharedHandlers({
		isOpen,
		props,
		searchTerm,
		isUpdatingFromWatch,
		clear,
		executeLocationAutocomplete,
		handleApplySearchTerm,
		baseHandleModelValueUpdate,
		afterSelectSuggestion: closeDialog,
		closeModal: closeDialog,
		triggerClearUsesFullClear: true
	});

function syncDialogOpen(open: boolean) {
	isOpen.value = open;
}

const dialogPanelHost = computed<SearchSuggestDialogPanelHostProps>(() => ({
	filterProps: props,
	isOpen: isOpen.value,
	variantStyles: variantStyles.value,
	dialogPlaceholder: SEARCH_PLACEHOLDERS.dialog,
	loading: loading.value,
	hasMinSearchTerm: hasMinSearchTerm.value,
	shouldShowEmptyState: shouldShowEmptyState.value,
	shouldShowLastResearchs: shouldShowLastResearchs.value,
	shouldShowCodeGroup: shouldShowCodeGroup.value,
	searchByCodeOption: searchByCodeOption.value,
	filteredLastResearchs: filteredLastResearchs.value,
	locationAutocompleteData: locationAutocompleteData.value,
	getDisplayValue,
	inputClasses: inputClasses.value,
	onCommandOpenChange: syncDialogOpen,
	onDialogBack: closeDialog,
	onDialogInputClear: handleInputClear,
	onModelValueUpdate: handleModelValueUpdate,
	onKeyDown: handleKeyDown,
	onRemoveResearch: removeResearchs
}));

const computedPlaceholder = computed<string | undefined>(() => {
	if (normalizedPlaceholder.value) {
		return normalizedPlaceholder.value;
	}

	if (import.meta.server) {
		return Array.isArray(computedPlaceholders.value)
			? computedPlaceholders.value?.[0] || ''
			: computedPlaceholders.value;
	}

	if (modelValue.value?.searchType === 'region') {
		return modelValue.value?.result.subtitle || '';
	}

	return Array.isArray(computedPlaceholders.value)
		? animatedPlaceholder.value
		: computedPlaceholders.value;
});

const selectedDisplayText = computed<string>(() => {
	if (dynamicPlaceholder.value) {
		return dynamicPlaceholder.value;
	}

	if (modelValue.value) {
		return getDisplayValue(modelValue.value);
	}
	return searchTerm.value || computedPlaceholder.value || '';
});

onMounted(() => {
	validateAndStartAnimation();
});

onBeforeUnmount(() => {
	pausePlaceholderAnimation();
	if (isOpen.value) {
		restoreBodyScroll();
	}
});

const panelRef =
	useTemplateRef<InstanceType<typeof SearchSuggestDialogPanel>>('panelRef');

function focusInput() {
	panelRef.value?.inputEl?.focus();
}

const { scheduleInputFocus, clearPendingInputFocus } = useDeferredInputFocus(
	() => focusInput()
);

// Trigger, keyboard shortcut or code: the input always takes the focus,
// otherwise the user has to tap it again to type.
watch(isOpen, open => {
	if (open) {
		scheduleInputFocus();
	} else {
		clearPendingInputFocus();
	}
});

defineExpose({ focusInput });
</script>
<template>
	<div class="relative h-full max-w-full overflow-hidden">
		<div
			v-if="showInputButton"
			:class="cn('flex size-full items-center', inputWrapperClasses)"
		>
			<slot name="input-prefix" />
			<slot
				name="trigger"
				:open="openDialog"
				:clear="handleInputClear"
				:is-open="isOpen"
			>
				<button
					:class="cn(variantStyles.buttonInput, buttonInputClasses)"
					@click="openDialog"
				>
					<span
						:class="
							cn(
								searchTerm || modelValue
									? variantStyles.resultText
									: variantStyles.placeholderText,
								'flex-1 text-left truncate',
								placeholderClasses
							)
						"
					>
						{{ selectedDisplayText }}
					</span>
				</button>
			</slot>
			<div class="ml-auto flex items-center">
				<slot name="suffix">
					<div
						:class="
							cn(
								'flex h-full w-7 items-center justify-end',
								inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							v-show="searchTerm"
							:class="
								cn(
									'size-5 min-w-5 cursor-pointer text-white-dark-3',
									inputClearIconClasses
								)
							"
							@click.stop="handleInputClear"
						/>
					</div>
					<div
						:class="
							cn(
								'grid h-full w-[62px] place-items-center',
								inputSearchIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightSearch
							:class="
								cn('size-7', variantStyles.searchIcon, inputSearchIconClasses)
							"
						/>
					</div>
				</slot>
			</div>
		</div>

		<SearchSuggestDialogPanel
			ref="panelRef"
			v-model:search-term="searchTerm"
			v-model:model-value="modelValue"
			v-model:selected-value="selectedValue"
			v-bind="dialogPanelHost"
		>
			<template v-if="$slots['empty-state-icon']" #empty-state-icon>
				<slot name="empty-state-icon" />
			</template>
		</SearchSuggestDialogPanel>
	</div>
</template>
